import { readHandoff } from './client';
import { parseCreationAttribution } from '@/lib/group/creation-attribution';
import type { GroupCreationAttribution } from '@/types/groups';

export type CreatorLink = {
  responseId: string;
  quizType: 'short' | 'long';
  ideologyName?: string;
};

export type CreatorDraft = {
  uid: string;
  token: string;
  expiresAt: number;
  creator: CreatorLink;
  attribution?: GroupCreationAttribution;
};

const STORAGE_KEY = 'votely_compare_creator_drafts_v1';
const DRAFT_TTL_MS = 30 * 60 * 1000;
const MAX_DRAFTS = 5;
const pending = new Map<string, Promise<CreatorDraft>>();

function isCreatorLink(value: unknown): value is CreatorLink {
  if (!value || typeof value !== 'object') return false;
  const link = value as Partial<CreatorLink>;
  return typeof link.responseId === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(link.responseId)
    && (link.quizType === 'short' || link.quizType === 'long')
    && (link.ideologyName === undefined || (typeof link.ideologyName === 'string' && link.ideologyName.length <= 200));
}

function readDrafts(): CreatorDraft[] {
  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const now = Date.now();
  return parsed.filter((draft): draft is CreatorDraft => !!draft && typeof draft === 'object'
    && typeof draft.uid === 'string' && typeof draft.token === 'string'
    && typeof draft.expiresAt === 'number' && draft.expiresAt > now
    && draft.expiresAt <= now + DRAFT_TTL_MS && isCreatorLink(draft.creator)).slice(-MAX_DRAFTS);
}

async function currentUid(): Promise<string> {
  // Read the cookie identity afresh, including on create. A draft must not
  // survive signing out into a different browser identity.
  const response = await fetch('/api/auth/me', { credentials: 'same-origin', cache: 'no-store' });
  if (!response.ok) throw new Error('Could not verify your current session.');
  const auth = await response.json() as { uid?: unknown };
  if (typeof auth.uid !== 'string' || !auth.uid) throw new Error('Could not verify your current session.');
  return auth.uid;
}

/** Restore only a locally consumed handoff for this token and cookie identity.
 * The token remains single-use on the server; this cache is UI state, never
 * evidence of result ownership. Session storage bounds it to this browser tab.
 */
export async function resolveCreatorDraft(token: string): Promise<CreatorDraft> {
  if (!/^[A-Za-z0-9_-]{1,256}$/.test(token)) throw new Error('This result link is invalid.');
  const uid = await currentUid();
  const existing = readDrafts().find((draft) => draft.uid === uid && draft.token === token);
  if (existing) return { ...existing, attribution: parseCreationAttribution(existing.attribution) };
  const key = JSON.stringify([uid, token]);
  const inflight = pending.get(key);
  if (inflight) return inflight;

  const consume = async (): Promise<CreatorDraft> => {
    const payload = await readHandoff<{ quizResponseId?: unknown; quizType?: unknown; ideologyName?: unknown; attribution?: unknown }>(token, 'quiz_to_compare');
    const creator = payload && {
      responseId: payload.quizResponseId,
      quizType: payload.quizType,
      ideologyName: payload.ideologyName,
    };
    if (!isCreatorLink(creator)) throw new Error('This result link is invalid or has expired.');
    const draft: CreatorDraft = { uid, token, creator, expiresAt: Date.now() + DRAFT_TTL_MS, attribution: parseCreationAttribution(payload?.attribution) };
    // Keep a few in-progress links so a slower previous token cannot overwrite
    // the current one. No answers or email addresses are stored in the draft.
    const others = readDrafts().filter((item) => item.uid !== uid || item.token !== token);
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...others, draft].slice(-MAX_DRAFTS)));
    return draft;
  };
  const operation = consume();
  pending.set(key, operation);
  try {
    return await operation;
  } finally {
    pending.delete(key);
  }
}

export function clearCreatorDraft(draft: CreatorDraft): void {
  try {
    const remaining = readDrafts().filter((item) => item.uid !== draft.uid || item.token !== draft.token);
    if (remaining.length) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(remaining));
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // A completed group must stay completed even if storage became unavailable.
  }
}
